/**
 * The one localised leaf type.
 *
 * Everything user-visible in a document is one of these. The decision it encodes — **localise at the
 * leaf, never the document** — is the single most expensive one to reverse: parallel per-locale
 * documents drift within a week, double the edit surface, and make "translate this page" a merge
 * rather than a field.
 *
 * ⚠️ THIS TYPE LIVED IN THE VENDOR'S DESIGN SYSTEM AND HAS BEEN MOVED HERE ON PURPOSE. In
 * `ali-web-services` it was exported by `@aliweb/ui`, so the *document model* imported the *admin
 * chrome* to describe a string. That coupling is what made the model un-runnable in a Worker. The
 * type is the model's, and the UI reads it — not the other way round.
 */

/**
 * A string in both languages, or one string that is the same in both (a brand name, a phone
 * number, an email address — an identifier rather than prose).
 *
 * ⚠️ `stale` NAMES THE LANGUAGE THAT IS NOW BEHIND THE OTHER, and it is here at the leaf for the
 * same reason the two strings are: it moves with the block, it dies with the block, and nothing has
 * to be reconciled when a block is duplicated, reordered or deleted. A sidecar map keyed by block id
 * would need every one of those operations to remember it, and the one that forgets leaves debt
 * pointing at a block that no longer exists.
 *
 * ⚠️ IT IS EDITOR BOOKKEEPING THAT TRAVELS IN THE DOCUMENT, WHICH IS ALLOWED PRECISELY BECAUSE IT
 * GRANTS NOTHING (CLAUDE.md §2.9). `resolve` ignores it, the renderer never reads it and the HTML
 * serialiser never emits it — a published page cannot be changed by its value. The moment it decides
 * something, it stops being bookkeeping and its rule moves to the server.
 */
export type Text = { en: string; ar: string; stale?: Lang } | { lit: string }

export type Lang = 'en' | 'ar'

export const LANGS: readonly Lang[] = ['en', 'ar'] as const

/** The writing direction for a language. Used to set `dir` on a root — never to pick a stylesheet.
 *  See CLAUDE.md §2.6: RTL is not a variant. */
export function dirFor(lang: Lang): 'ltr' | 'rtl' {
  return lang === 'ar' ? 'rtl' : 'ltr'
}

/**
 * Read a `Text` in a language.
 *
 * ⚠️ IT FALLS BACK TO THE OTHER LANGUAGE RATHER THAN TO EMPTY, and that is a product decision. A
 * half-translated site is the normal state of a site somebody is still building: the customer wrote
 * the English hero and has not reached the Arabic one yet. Rendering a blank heading would tell them
 * their site is broken; rendering the English one tells them what is missing and where. `validateSite`
 * is what reports untranslated copy at publish — a *warning*, not a refusal.
 */
export function resolve(value: Text, lang: Lang): string {
  if (isLit(value)) return value.lit
  const wanted = value[lang]
  if (wanted) return wanted
  return lang === 'ar' ? value.en : value.ar
}

export function isLit(value: Text): value is { lit: string } {
  return 'lit' in value
}

/** Whether a `Text` carries any copy at all, in either language. Used to drop empty optional
 *  headings and buttons rather than render a blank node — an empty `<h2>` still takes its margin. */
export function hasText(value: Text | undefined | null): boolean {
  if (!value) return false
  return isLit(value) ? value.lit.length > 0 : value.en.length > 0 || value.ar.length > 0
}

/** Whether a `Text` is missing one of its two languages. Publish reports these; the editor shows
 *  them as a per-field hint. */
export function isPartial(value: Text): boolean {
  if (isLit(value)) return false
  const both = value.en.length > 0 && value.ar.length > 0
  const neither = value.en.length === 0 && value.ar.length === 0
  return !both && !neither
}

/** A structural check, for the validator. Not exported as a type guard on purpose: a value that
 *  fails this is reported with a path, not narrowed away. */
export function isText(value: unknown): value is Text {
  if (typeof value !== 'object' || value === null) return false
  const o = value as Record<string, unknown>
  if ('lit' in o) return typeof o.lit === 'string'
  if (typeof o.en !== 'string' || typeof o.ar !== 'string') return false
  // ⚠️ A BAD `stale` FAILS THE WHOLE VALUE rather than being quietly dropped. It arrives from the
  // same PUT body as the copy does, and a validator that accepts anything in a field it does not
  // understand is how a document ends up carrying somebody else's data.
  return o.stale === undefined || o.stale === 'en' || o.stale === 'ar'
}

/** Build a `Text` from one string used in both languages. For seeds and defaults only — never for
 *  customer copy, which must be able to differ. */
export function lit(value: string): Text {
  return { lit: value }
}

export const EMPTY_TEXT: Text = { en: '', ar: '' }

/**
 * Apply an edit in one language, and work out what that did to the other.
 *
 * The editor shows one language at a time, so the customer cannot see the copy they are putting out
 * of date. This is what remembers for them.
 *
 * ⚠️ EDITING THE LANGUAGE THAT IS ALREADY BEHIND CLEARS THE DEBT AND DOES NOT CREATE THE MIRROR OF
 * IT. Without that asymmetry the flag never converges: English marks Arabic, then Arabic marks
 * English, and every field in the site is permanently out of date in one direction or the other.
 * The rule is directional — "this one is catching up" is a different act from "this one is moving
 * ahead", and only the second creates debt.
 *
 * ⚠️ AND AN EMPTY COUNTERPART IS MISSING, NOT STALE. `isPartial` already reports untranslated copy
 * at publish; marking it stale as well would say the same thing twice in two vocabularies, and the
 * customer would be offered "update the translation" for a translation that was never written.
 */
export function edited(value: Text, lang: Lang, next: string): Text {
  if (isLit(value)) return { lit: next }

  const other: Lang = lang === 'en' ? 'ar' : 'en'
  const { stale: _drop, ...rest } = value
  const updated = { ...rest, [lang]: next } as { en: string; ar: string; stale?: Lang }

  if (value.stale === lang) return updated
  if (updated[other]) updated.stale = other
  return updated
}

/** Which language of this value is behind the other, if any. */
export function staleLang(value: Text): Lang | null {
  if (isLit(value)) return null
  return value.stale ?? null
}

/** Accept a translation without claiming the source changed — the debt is settled, not moved. */
export function translated(value: Text, lang: Lang, next: string): Text {
  if (isLit(value)) return { lit: next }
  const { stale, ...rest } = value
  const updated = { ...rest, [lang]: next } as { en: string; ar: string; stale?: Lang }
  if (stale && stale !== lang) updated.stale = stale
  return updated
}
